/**
 * Pure hex-math utilities for the open-board pointy-top axial grid.
 *
 * Source of truth: docs/task-log/20260505-cascadia-online/02-rules-spec.md §4.
 *
 * Rules:
 * - No mutations — all functions return new objects / new arrays.
 * - No I/O, no Math.random().
 * - No import of `.js`/`.ts` extensions.
 */

import {
  type AxialCoord,
  coordKey,
  type EdgeIndex,
  NEIGHBOUR_OFFSETS,
} from "../types/coords";
import type { Habitat } from "../types/habitat";
import type { TileRotation } from "../types/tile";

/**
 * Canonical tile-rotation geometry (Foundation rule — shared by the engine's
 * end-game area scoring and the renderers). A tile rotated clockwise by
 * `rotation` steps (1 step = 60°) shows, on physical edge `i`, the def's
 * edge `(i - rotation + 6) % 6`. Identity at `rotation === 0`, so pre-rotation
 * games / replays (no rotation field ⇒ 0) are byte-for-byte unaffected.
 *
 * Pure — returns a new tuple, never mutates `edges`.
 */
export const rotatedEdges = (
  edges: readonly Habitat[],
  rotation: TileRotation,
): readonly Habitat[] => {
  const n = edges.length;
  return edges.map((_, i) => edges[(i - rotation + n) % n]!);
};

/**
 * Returns the 6 neighbours of `coord` in edge-index order (0..5).
 * Order matches `NEIGHBOUR_OFFSETS` from `types/coords.ts`.
 */
export const getNeighbours = (coord: AxialCoord): readonly AxialCoord[] =>
  NEIGHBOUR_OFFSETS.map(({ dq, dr }) => ({
    q: coord.q + dq,
    r: coord.r + dr,
  }));

/**
 * Returns the single neighbour of `coord` that lies across edge `edge`.
 */
export const getNeighbourAtEdge = (
  coord: AxialCoord,
  edge: EdgeIndex,
): AxialCoord => {
  const offset = NEIGHBOUR_OFFSETS[edge];
  return { q: coord.q + offset.dq, r: coord.r + offset.dr };
};

/**
 * Hex distance using cube-coordinate conversion.
 *
 * Axial → cube: s = -q - r
 * Distance = max(|Δq|, |Δr|, |Δs|)
 */
export const axialDistance = (a: AxialCoord, b: AxialCoord): number => {
  const dq = Math.abs(a.q - b.q);
  const dr = Math.abs(a.r - b.r);
  const ds = Math.abs(-a.q - a.r - (-b.q - b.r));
  return Math.max(dq, dr, ds);
};

/**
 * BFS from `start` over axial neighbours, admitting each visited coord only if
 * it passes `isMember`. Returns the connected component containing `start`.
 *
 * - `isMember` is the sole membership predicate; the universe is whatever
 *   it encloses (typically a `cells: Record<coordKey, …>` lookup).
 * - `start` is included in the result if `isMember(start)` is true.
 * - The returned array is a new array; nothing external is mutated.
 */
export const getConnectedRegion = (
  start: AxialCoord,
  isMember: (c: AxialCoord) => boolean,
): readonly AxialCoord[] => {
  const visited = new Set<string>();
  const result: AxialCoord[] = [];
  const queue: AxialCoord[] = [start];
  visited.add(coordKey(start));

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (isMember(current)) {
      result.push(current);
      for (const neighbour of getNeighbours(current)) {
        const key = coordKey(neighbour);
        if (!visited.has(key)) {
          visited.add(key);
          queue.push(neighbour);
        }
      }
    }
  }

  return result;
};

/**
 * Partitions all coords in `coords` into connected components, where two
 * adjacent coords belong to the same component only if they share the same
 * `groupKey` value.
 *
 * - Each returned region is non-empty.
 * - Regions are sorted by the minimum `coordKey` within the region
 *   (deterministic output regardless of input order).
 * - Input is never mutated.
 */
export const getAllConnectedRegions = (
  coords: Iterable<AxialCoord>,
  groupKey: (c: AxialCoord) => string,
): readonly (readonly AxialCoord[])[] => {
  const allCoords = Array.from(coords);
  if (allCoords.length === 0) {
    return [];
  }

  // Build a map from coordKey → coord for fast neighbour lookup
  const coordMap = new Map<string, AxialCoord>();
  for (const c of allCoords) {
    coordMap.set(coordKey(c), c);
  }

  const unvisited = new Set<string>(coordMap.keys());
  const regions: Array<readonly AxialCoord[]> = [];

  for (const c of allCoords) {
    const key = coordKey(c);
    if (!unvisited.has(key)) {
      continue;
    }

    // BFS within the same groupKey
    const myGroup = groupKey(c);
    const region: AxialCoord[] = [];
    const queue: AxialCoord[] = [c];
    unvisited.delete(key);

    while (queue.length > 0) {
      const current = queue.shift()!;
      region.push(current);

      for (const neighbour of getNeighbours(current)) {
        const nKey = coordKey(neighbour);
        if (unvisited.has(nKey)) {
          const neighbourCoord = coordMap.get(nKey);
          if (
            neighbourCoord !== undefined &&
            groupKey(neighbourCoord) === myGroup
          ) {
            unvisited.delete(nKey);
            queue.push(neighbourCoord);
          }
        }
      }
    }

    if (region.length > 0) {
      regions.push(region);
    }
  }

  // Sort regions by minimum coordKey within each region (deterministic)
  regions.sort((a, b) => {
    const minA = a.map(coordKey).sort()[0] ?? "";
    const minB = b.map(coordKey).sort()[0] ?? "";
    return minA < minB ? -1 : minA > minB ? 1 : 0;
  });

  return regions;
};
